import React from 'react';
import PropTypes from 'prop-types';
import { Navigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Container, Typography, Alert, Box, Button } from '@mui/material';
import usePermissions from '../hooks/usePermissions';
import DWLFLoader from './DWLFLoader';

/**
 * Route wrapper that protects routes based on authentication and permissions
 *
 * @example
 * // Require authentication only
 * <Route path="/dashboard" element={
 *   <ProtectedRoute requireAuth>
 *     <Dashboard />
 *   </ProtectedRoute>
 * } />
 *
 * @example
 * // Require specific permission
 * <Route path="/admin" element={
 *   <ProtectedRoute permission="admin:read">
 *     <AdminPage />
 *   </ProtectedRoute>
 * } />
 *
 * @example
 * // Require any of multiple permissions
 * <Route path="/roles" element={
 *   <ProtectedRoute permission={['admin:read', 'roles:read']}>
 *     <RolesPage />
 *   </ProtectedRoute>
 * } />
 *
 * @example
 * // Require all permissions
 * <Route path="/roles/edit" element={
 *   <ProtectedRoute permission={['roles:read', 'roles:write']} requireAll>
 *     <RoleEditor />
 *   </ProtectedRoute>
 * } />
 */
const ProtectedRoute = ({
  children,
  requireAuth = false,
  permission,
  requireAll = false,
  redirectTo = '/login',
  showAccessDenied = true,
}) => {
  const location = useLocation();
  const user = useSelector((state) => state.account.user);
  const { hasPermission, hasAnyPermission, hasAllPermission, loading, loaded } = usePermissions();

  // Check authentication if required, or if permission check requires a logged-in user
  if ((requireAuth || permission) && !user) {
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  // If permission check is required
  if (permission) {
    // Show loading while permissions are being fetched
    if (loading || !loaded) {
      return (
        <Container maxWidth="lg" sx={{ py: 6 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <DWLFLoader size="small" showText={false} />
            <Typography color="text.secondary">
              Checking permissions...
            </Typography>
          </Box>
        </Container>
      );
    }

    // Determine if user has required permissions
    let hasAccess = false;
    if (Array.isArray(permission)) {
      hasAccess = requireAll
        ? hasAllPermission(permission)
        : hasAnyPermission(permission);
    } else {
      hasAccess = hasPermission(permission);
    }

    // Show access denied if user lacks permission
    if (!hasAccess) {
      if (!showAccessDenied) {
        return <Navigate to="/" replace />;
      }

      return (
        <Container maxWidth="lg" sx={{ py: 6 }}>
          <Typography variant="h4" gutterBottom>
            Access Denied
          </Typography>
          <Alert severity="warning" sx={{ mb: 3 }}>
            You don&apos;t have permission to access this page.
            {Array.isArray(permission)
              ? ` Required: ${requireAll ? 'all of' : 'any of'} [${permission.join(', ')}]`
              : ` Required: ${permission}`}
          </Alert>
          <Button variant="contained" href="/">
            Return to Home
          </Button>
        </Container>
      );
    }
  }

  return children;
};

ProtectedRoute.propTypes = {
  children: PropTypes.node.isRequired,
  requireAuth: PropTypes.bool,
  permission: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]),
  requireAll: PropTypes.bool,
  redirectTo: PropTypes.string,
  showAccessDenied: PropTypes.bool,
};

export default ProtectedRoute;
